import { useTranslation } from 'react-i18next'
import {
  useDeleteLvdComplianceFileSlotAttachment,
  useGetLvdComplianceFileSlot,
  useUploadLvdComplianceFileSlot,
  type LvdProductComplianceFileEvidenceSlot,
} from '@/shared/hooks/api/lvdProductComplianceFileAttachments'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { ProductComplianceFileSlotStep } from '@/v2/components/product-compliance-briefing/ProductComplianceFileSlotStep'
import type { LvdBriefingStep } from '../../utils/lvdLegislationBriefing'
import { V2LvdLegislationBriefingStepShell } from './V2LvdLegislationBriefingStepShell'

const LVD_SLOT_STEP_KEYS: Record<
  LvdProductComplianceFileEvidenceSlot,
  | 'productDrawings'
  | 'standardsTestReport'
  | 'examinationsOrCalculations'
  | 'instructionsAndSafetyInformation'
> = {
  DRAWINGS_SCHEMAS: 'productDrawings',
  STANDARDS_TEST_REPORT: 'standardsTestReport',
  EXAMINATIONS_OR_CALCULATIONS: 'examinationsOrCalculations',
  INSTRUCTIONS_AND_SAFETY_INFORMATION: 'instructionsAndSafetyInformation',
}

export function V2LvdComplianceFileSlotStep({
  step,
  index,
  productId,
  slotKind,
}: {
  step: LvdBriefingStep
  index: number
  productId: string
  slotKind: LvdProductComplianceFileEvidenceSlot
}) {
  const { t } = useTranslation('common')
  const canWrite = useCanWriteOrgData()
  const evidenceSlotTitle = t(
    `v2.pages.products.detail.lvdBriefing.steps.items.${LVD_SLOT_STEP_KEYS[slotKind]}.title`
  )
  const { data, isLoading } = useGetLvdComplianceFileSlot(productId, slotKind)
  const { mutateAsync: uploadFile } = useUploadLvdComplianceFileSlot(
    productId,
    slotKind
  )
  const { mutateAsync: deleteFile, isPending: isDeletePending } =
    useDeleteLvdComplianceFileSlotAttachment(productId, slotKind)

  return (
    <ProductComplianceFileSlotStep
      canWrite={canWrite}
      isLoading={isLoading}
      files={data?.files ?? []}
      isDeletePending={isDeletePending}
      evidenceSlotTitle={evidenceSlotTitle}
      onUploadFile={uploadFile}
      onDeleteFile={deleteFile}
      stepShell={({ actions, children }) => (
        <V2LvdLegislationBriefingStepShell
          step={step}
          index={index}
          actions={actions}
        >
          {children}
        </V2LvdLegislationBriefingStepShell>
      )}
    />
  )
}
